import { Injectable } from '@angular/core';
import { combineLatest, map, mergeMap, Observable, of, take } from 'rxjs';
import cloneDeep from 'lodash.clonedeep';

import {
  DprActivitiesGridSyncResult,
  DprHseGridSync,
  DprPersonnelGridSync,
  DprWeatherGridSync,
  DprConsumableGridSync,
  DprSummaryGridSync,
  DprProgressGridSync,
  DprAppendixGridSync,
  DprAppendixGridSyncResult,
  DprHiddenGridSync,
  WriteDprActivity,
  WriteDprParallelActivity,
  DprForecastActivitiesGridSync,
  DprForecastActivitiesGridSyncResult,
  DprStatusTrackerGridSync,
  DprProgressWithoutTargetGridSync,
  DprFreeTextFieldSync,
  DprFreeTextFieldSyncResult,
  DprHiddenGridSyncResult,
  DprSummaryGridSyncResult,
  DprTableOfColumnGridSync,
} from '@dpr-details/models/sync';
import {
  createEmptyDprStatusTracker,
  createEmptyDprTableOfColumns,
  withoutDerivedStatusTrackerCells,
} from '@dpr-details/utils';
import {
  DprWeather,
  DprPersonnel,
  DprConsumable,
  DprHse,
  DprHseDetail,
  DprSummary,
  DprActivity,
  DprDetails,
  DprHidden,
  DprProgress,
  DprForecastActivity,
  DprStatusTracker,
  DprStatusTrackerDetails,
  DprTableOfColumns,
  DprProgressWithoutTarget,
  DprFreeTextField,
} from '@shared/models/dpr';
import { Sync } from '@shared/models/grid';
import { StatusTrackerWithData } from '@shared/models/project-setup';
import { MergeService } from '@shared/services';
import { DprDetailsWarningService } from '@shared/services/dpr';
import { OfflineFacade, UserDetailsFacade } from '@shared/state/facades';
import {
  DprPersonnelOfflineService,
  DprConsumableOfflineService,
  DprProgressOfflineService,
  DprProgressWithoutTargetOfflineService,
  DprHseOfflineService,
  DprAccumulationOfflineService,
} from '.';

@Injectable()
export class DprDetailsOfflineSaveService {
  constructor(
    private mergeService: MergeService,
    private dprDetailsWarningService: DprDetailsWarningService,
    private offlineFacade: OfflineFacade,
    private userDetailsFacade: UserDetailsFacade,
    private dprPersonnelOfflineService: DprPersonnelOfflineService,
    private dprConsumableOfflineService: DprConsumableOfflineService,
    private dprHseOfflineService: DprHseOfflineService,
    private dprProgressOfflineService: DprProgressOfflineService,
    private dprProgressWithoutTargetService: DprProgressWithoutTargetOfflineService,
    private dprAccumulationOfflineService: DprAccumulationOfflineService
  ) {}

  applyDprActivityOfflineChanges(
    syncDprActivities: Sync<WriteDprActivity>,
    dprDetailId: number | string
  ): Observable<DprActivitiesGridSyncResult> {
    return this.offlineFacade.getDprDetails(dprDetailId).pipe(
      take(1),
      mergeMap((dprDetails) => {
        // create a deep copy to make the object editable
        let dprActivities = cloneDeep(
          dprDetails?.dprActivities.filter((x) => !isNaN(+x.id))
        ) as DprActivity[];

        syncDprActivities.addedList.forEach((added) => {
          const newItem: DprActivity = {
            id: added.id,
            dprDetailId: added.dprDetailId,
            activityGroupId: added.activityGroupId,
            activityId: added.activityId,
            locationId: added.locationId,
            start: added.start,
            finish: added.finish,
            comment: added.comment,
            dprSubActivities: added.syncDprSubActivities?.addedList ?? [],
            dprActivityCustomDataList:
              added.syncDprActivityCustomDataList?.addedList ?? [],
            dprActivityAttachments:
              added.syncDprActivityAttachments?.addedList ?? [],
          };
          dprActivities.push(newItem);
        });

        syncDprActivities.updatedList.forEach((updated) => {
          const dprActivity = dprActivities.find((a: DprActivity) => {
            return a.id === updated.id;
          });
          if (dprActivity) {
            dprActivity.dprDetailId = updated.dprDetailId;
            dprActivity.activityGroupId = updated.activityGroupId;
            dprActivity.activityId = updated.activityId;
            dprActivity.locationId = updated.locationId;
            dprActivity.start = updated.start;
            dprActivity.finish = updated.finish;
            dprActivity.comment = updated.comment;

            dprActivity.dprSubActivities = this.mergeService.applyChanges(
              dprActivity.dprSubActivities,
              updated.syncDprSubActivities
            );
            dprActivity.dprActivityCustomDataList =
              this.mergeService.applyChanges(
                dprActivity.dprActivityCustomDataList,
                updated.syncDprActivityCustomDataList
              );
            dprActivity.dprActivityAttachments = this.mergeService.applyChanges(
              dprActivity.dprActivityAttachments,
              updated.syncDprActivityAttachments
            );
          }
        });

        dprActivities = this.mergeService.applyDeleteChanges(
          dprActivities,
          syncDprActivities.deletedIds
        );

        return of({
          dprActivities,
          dprParallelActivities: null,
          firstWarning: dprDetails.firstWarning,
          warningStatusTab: dprDetails.warningStatusTab,
        });
      })
    );
  }

  applyDprParallelActivityOfflineChanges(
    syncDprParallelActivities: Sync<WriteDprParallelActivity>,
    dprDetailId: number | string
  ): Observable<DprActivitiesGridSyncResult> {
    return this.offlineFacade.getDprDetails(dprDetailId).pipe(
      take(1),
      mergeMap((dprDetails) => {
        // create a deep copy to make the object editable
        let dprParallelActivities = cloneDeep(
          dprDetails?.dprParallelActivities.filter((x) => !isNaN(+x.id))
        ) as DprActivity[];

        syncDprParallelActivities.addedList.forEach((added) => {
          const newItem: DprActivity = {
            id: added.id,
            dprDetailId: added.dprDetailId,
            activityGroupId: added.activityGroupId,
            activityId: added.activityId,
            locationId: added.locationId,
            start: added.start,
            finish: added.finish,
            comment: added.comment,
            parallelActivityType: added.parallelActivityType,
            dprParallelSubActivityList:
              added.syncDprParallelSubActivities?.addedList ?? [],
            dprParallelActivityCustomDataList:
              added.syncDprParallelActivityCustomDataList?.addedList ?? [],
            dprParallelActivityAttachments:
              added.syncDprParallelActivityAttachments?.addedList ?? [],
          };
          dprParallelActivities.push(newItem);
        });

        syncDprParallelActivities.updatedList.forEach((updated) => {
          const dprActivity = dprParallelActivities.find((a: DprActivity) => {
            return a.id === updated.id;
          });
          if (dprActivity) {
            dprActivity.dprDetailId = updated.dprDetailId;
            dprActivity.activityGroupId = updated.activityGroupId;
            dprActivity.activityId = updated.activityId;
            dprActivity.locationId = updated.locationId;
            dprActivity.start = updated.start;
            dprActivity.finish = updated.finish;
            dprActivity.comment = updated.comment;
            dprActivity.parallelActivityType = updated.parallelActivityType;

            dprActivity.dprParallelSubActivityList =
              this.mergeService.applyChanges(
                dprActivity.dprParallelSubActivityList,
                updated.syncDprParallelSubActivities
              );
            dprActivity.dprParallelActivityCustomDataList =
              this.mergeService.applyChanges(
                dprActivity.dprParallelActivityCustomDataList,
                updated.syncDprParallelActivityCustomDataList
              );
            dprActivity.dprParallelActivityAttachments =
              this.mergeService.applyChanges(
                dprActivity.dprParallelActivityAttachments,
                updated.syncDprParallelActivityAttachments
              );
          }
        });

        dprParallelActivities = this.mergeService.applyDeleteChanges(
          dprParallelActivities,
          syncDprParallelActivities.deletedIds
        );

        return of({
          dprActivities: null,
          dprParallelActivities,
          firstWarning: dprDetails.firstWarning,
          warningStatusTab: dprDetails.warningStatusTab,
        });
      })
    );
  }

  applyDprStatusTrackerOfflineChanges(
    syncDprStatusTracker: DprStatusTrackerGridSync
  ): Observable<DprStatusTracker> {
    return combineLatest([
      this.offlineFacade.getDprDetails(
        syncDprStatusTracker.dprStatusTracker.dprDetailId
      ),
      this.userDetailsFacade.query.projectSetup$,
    ]).pipe(
      take(1),
      mergeMap(([dprDetails, projectSetup]) => {
        const dprStatusTracker = cloneDeep(
          dprDetails?.dprStatusTracker ??
            createEmptyDprStatusTracker(
              syncDprStatusTracker.dprStatusTracker.dprDetailId
            )
        ) as DprStatusTracker;
        const syncDetails =
          syncDprStatusTracker.dprStatusTracker.syncDprStatusTrackerDetails;

        // A section that had no id yet takes the one the save was made with.
        dprStatusTracker.id = syncDprStatusTracker.dprStatusTracker.id;

        dprStatusTracker.dprStatusTrackerDetails =
          dprStatusTracker.dprStatusTrackerDetails.filter((x) => !isNaN(+x.id));

        dprStatusTracker.dprStatusTrackerDetails =
          this.withoutEditedDerivedCells(
            this.mergeService.applyChanges(
              dprStatusTracker.dprStatusTrackerDetails,
              syncDetails
            ),
            dprStatusTracker.dprStatusTrackerDetails,
            syncDetails,
            projectSetup?.statusTrackers
          );

        const updatedDprDetails = {
          ...cloneDeep(dprDetails),
          dprStatusTracker,
        } as DprDetails;

        this.dprDetailsWarningService.updateDprWarningStatus(updatedDprDetails);
        updatedDprDetails.dprStatusTracker.firstWarning =
          updatedDprDetails.firstWarning;
        updatedDprDetails.dprStatusTracker.warningStatusTab =
          updatedDprDetails.warningStatusTab;

        return this.dprAccumulationOfflineService
          .updateDependentPreviousValues(updatedDprDetails, {
            kind: 'statusTracker',
          })
          .pipe(map(() => updatedDprDetails.dprStatusTracker));
      })
    );
  }

  applyDprWeatherOfflineChanges(
    syncDprWeather: DprWeatherGridSync
  ): Observable<DprWeather> {
    return this.offlineFacade
      .getDprDetails(syncDprWeather.dprWeather.dprDetailId)
      .pipe(
        take(1),
        mergeMap((dprDetails) => {
          // create a deep copy to make the object editable
          const dprWeather = cloneDeep(dprDetails?.dprWeather) as DprWeather;

          dprWeather.dprWeatherDetails = dprWeather.dprWeatherDetails.filter(
            (x) => !isNaN(+x.id)
          );
          dprWeather.dprWeatherAttachments =
            dprWeather.dprWeatherAttachments.filter((x) => !isNaN(+x.id));

          dprWeather.dprWeatherDetails = this.mergeService.applyChanges(
            dprWeather.dprWeatherDetails,
            syncDprWeather.dprWeather.syncDprWeatherDetails
          );
          dprWeather.dprWeatherAttachments = this.mergeService.applyChanges(
            dprWeather.dprWeatherAttachments,
            syncDprWeather.dprWeather.syncDprWeatherAttachments
          );
          dprWeather.comment = syncDprWeather.dprWeather.comment;

          const updatedDprDetails = {
            ...cloneDeep(dprDetails),
            dprWeather,
          } as DprDetails;

          this.dprDetailsWarningService.updateDprWarningStatus(
            updatedDprDetails
          );
          updatedDprDetails.dprWeather.firstWarning =
            updatedDprDetails.firstWarning;

          updatedDprDetails.dprWeather.warningStatusTab =
            updatedDprDetails.warningStatusTab;

          return of(updatedDprDetails.dprWeather);
        })
      );
  }

  applyDprPersonnelOfflineChanges(
    syncDprPersonnel: DprPersonnelGridSync
  ): Observable<DprPersonnel> {
    return this.offlineFacade
      .getDprDetails(syncDprPersonnel.dprPersonnel.dprDetailId)
      .pipe(
        take(1),
        mergeMap((dprDetails) => {
          // create a deep copy to make the object editable
          const dprPersonnel = cloneDeep(
            dprDetails?.dprPersonnel
          ) as DprPersonnel;

          dprPersonnel.dprPersonnelAttachments =
            dprPersonnel.dprPersonnelAttachments.filter((x) => !isNaN(+x.id));

          syncDprPersonnel.dprPersonnel.syncDprPersonnelDetails.addedList.forEach(
            (added) => {
              const index = dprPersonnel.dprPersonnelDetails.findIndex(
                (x) => x.personnelId === added.personnelId
              );

              if (index === -1) {
                dprPersonnel.dprPersonnelDetails.push(added);
                return;
              } else {
                dprPersonnel.dprPersonnelDetails[index] = added;
              }
            }
          );

          dprPersonnel.dprPersonnelDetails =
            this.mergeService.applyUpdateChanges(
              dprPersonnel.dprPersonnelDetails,
              syncDprPersonnel.dprPersonnel.syncDprPersonnelDetails.updatedList
            );
          dprPersonnel.dprPersonnelDetails =
            this.mergeService.applyDeleteChanges(
              dprPersonnel.dprPersonnelDetails,
              syncDprPersonnel.dprPersonnel.syncDprPersonnelDetails.deletedIds
            );
          dprPersonnel.dprPersonnelAttachments = this.mergeService.applyChanges(
            dprPersonnel.dprPersonnelAttachments,
            syncDprPersonnel.dprPersonnel.syncDprPersonnelAttachments
          );
          dprPersonnel.comment = syncDprPersonnel.dprPersonnel.comment;

          const updatedDprDetails = {
            ...cloneDeep(dprDetails),
            dprPersonnel,
          } as DprDetails;

          // this is needed for the dpr offline personnel service to have the
          // right previous value
          this.offlineFacade.setDprPersonnel(
            cloneDeep(dprPersonnel),
            updatedDprDetails.id
          );

          return combineLatest([
            this.dprPersonnelOfflineService.updateDependentPersonnelPrevValues(
              dprDetails.dprDateTime
            ),
            of(updatedDprDetails),
          ]);
        }),
        mergeMap(([_, updatedDprDetails]) => {
          this.dprDetailsWarningService.updateDprWarningStatus(
            updatedDprDetails
          );
          updatedDprDetails.dprPersonnel.firstWarning =
            updatedDprDetails.firstWarning;
          updatedDprDetails.dprPersonnel.warningStatusTab =
            updatedDprDetails.warningStatusTab;

          return of(updatedDprDetails.dprPersonnel);
        })
      );
  }

  applyDprConsumableOfflineChanges(
    syncDprConsumable: DprConsumableGridSync
  ): Observable<DprConsumable> {
    return this.offlineFacade
      .getDprDetails(syncDprConsumable.dprConsumable.dprDetailId)
      .pipe(
        take(1),
        mergeMap((dprDetails) => {
          // create a deep copy to make the object editable
          const dprConsumable = cloneDeep(
            dprDetails?.dprConsumable
          ) as DprConsumable;

          dprConsumable.dprConsumableAttachments =
            dprConsumable.dprConsumableAttachments.filter((x) => !isNaN(+x.id));

          syncDprConsumable.dprConsumable.syncDprConsumableDetails.addedList.forEach(
            (added) => {
              const index = dprConsumable.dprConsumableDetails.findIndex(
                (x) => x.consumableId === added.consumableId
              );

              if (index === -1) {
                dprConsumable.dprConsumableDetails.push(added);
                return;
              } else {
                dprConsumable.dprConsumableDetails[index] = added;
              }
            }
          );

          dprConsumable.dprConsumableDetails =
            this.mergeService.applyUpdateChanges(
              dprConsumable.dprConsumableDetails,
              syncDprConsumable.dprConsumable.syncDprConsumableDetails
                .updatedList
            );
          dprConsumable.dprConsumableDetails =
            this.mergeService.applyDeleteChanges(
              dprConsumable.dprConsumableDetails,
              syncDprConsumable.dprConsumable.syncDprConsumableDetails
                .deletedIds
            );
          dprConsumable.dprConsumableAttachments =
            this.mergeService.applyChanges(
              dprConsumable.dprConsumableAttachments,
              syncDprConsumable.dprConsumable.syncDprConsumableAttachments
            );
          dprConsumable.comment = syncDprConsumable.dprConsumable.comment;

          const updatedDprDetails = {
            ...cloneDeep(dprDetails),
            dprConsumable,
          } as DprDetails;

          // this is needed for the dpr offline consumable service to have the
          // right previous value
          this.offlineFacade.setDprConsumable(
            cloneDeep(dprConsumable),
            updatedDprDetails.id
          );

          return combineLatest([
            this.dprConsumableOfflineService.updateDependentConsumablePrevValues(
              dprDetails.dprDateTime
            ),
            of(updatedDprDetails),
          ]);
        }),
        mergeMap(([_, updatedDprDetails]) => {
          this.dprDetailsWarningService.updateDprWarningStatus(
            updatedDprDetails
          );
          updatedDprDetails.dprConsumable.firstWarning =
            updatedDprDetails.firstWarning;
          updatedDprDetails.dprConsumable.warningStatusTab =
            updatedDprDetails.warningStatusTab;

          return of(updatedDprDetails.dprConsumable);
        })
      );
  }

  applyDprHseOfflineChanges(syncDprHse: DprHseGridSync): Observable<DprHse> {
    return this.offlineFacade.getDprDetails(syncDprHse.dprHse.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetails) => {
        // create a deep copy to make the object editable
        const dprHSE = cloneDeep(dprDetails?.dprHSE) as DprHse;
        dprHSE.dprhseAttachments = dprHSE.dprhseAttachments.filter(
          (x) => !isNaN(+x.id)
        );

        syncDprHse.dprHse.syncDprHseDetails.addedList.forEach((added) => {
          const index = dprHSE.dprhseDetails.findIndex(
            (x) => x.hseId === added.hseId
          );

          if (index === -1) {
            dprHSE.dprhseDetails.push(added as DprHseDetail);
          } else {
            dprHSE.dprhseDetails[index] = added as DprHseDetail;
          }
        });

        dprHSE.dprhseDetails = this.mergeService.applyUpdateChanges(
          dprHSE.dprhseDetails,
          syncDprHse.dprHse.syncDprHseDetails.updatedList
        );
        dprHSE.dprhseDetails = this.mergeService.applyDeleteChanges(
          dprHSE.dprhseDetails,
          syncDprHse.dprHse.syncDprHseDetails.deletedIds
        );
        dprHSE.dprhseAttachments = this.mergeService.applyChanges(
          dprHSE.dprhseAttachments,
          syncDprHse.dprHse.syncDprHseAttachments
        );
        dprHSE.comment = syncDprHse.dprHse.comment;

        const updatedDprDetails = {
          ...cloneDeep(dprDetails),
          dprHSE,
        } as DprDetails;

        // this is needed for the dpr offline hse service to have the
        // right previous value
        this.offlineFacade.setDprHse(cloneDeep(dprHSE), updatedDprDetails.id);

        return combineLatest([
          this.dprHseOfflineService.updateDependentHsePrevValues(
            dprDetails.dprDateTime
          ),
          of(updatedDprDetails),
        ]);
      }),
      mergeMap(([_, updatedDprDetails]) => {
        this.dprDetailsWarningService.updateDprWarningStatus(updatedDprDetails);
        updatedDprDetails.dprHSE.firstWarning = updatedDprDetails.firstWarning;
        updatedDprDetails.dprHSE.warningStatusTab =
          updatedDprDetails.warningStatusTab;

        return of(updatedDprDetails.dprHSE);
      })
    );
  }

  applyDprSummaryOfflineChanges(
    syncDprSummary: DprSummaryGridSync
  ): Observable<DprSummaryGridSyncResult> {
    return this.offlineFacade
      .getDprDetails(syncDprSummary.dprSummary.dprDetailId)
      .pipe(
        take(1),
        mergeMap((dprDetails) => {
          const dprSummary = cloneDeep(dprDetails.dprSummary) as DprSummary;
          dprSummary.dprSummaryAttachments =
            dprSummary.dprSummaryAttachments.filter((x) => !isNaN(+x.id));

          dprSummary.currentComment = syncDprSummary.dprSummary.currentComment;
          dprSummary.nextComment = syncDprSummary.dprSummary.nextComment;
          dprSummary.externalComment =
            syncDprSummary.dprSummary.externalComment;

          dprSummary.dprSummaryAttachments = this.mergeService.applyChanges(
            dprSummary.dprSummaryAttachments,
            syncDprSummary.dprSummary.syncDprSummaryAttachments
          );

          const updatedDprDetails = {
            ...cloneDeep(dprDetails),
            dprSummary,
          } as DprDetails;

          return of({
            dprDetailId: syncDprSummary.dprSummary.dprDetailId,
            firstWarning: updatedDprDetails.firstWarning,
            warningStatusTab: dprDetails.warningStatusTab,
            dprSummary: updatedDprDetails.dprSummary,
          });
        })
      );
  }

  applyDprForecastActivityOfflineChanges(
    model: DprForecastActivitiesGridSync
  ): Observable<DprForecastActivitiesGridSyncResult> {
    return this.offlineFacade.getDprDetails(model.dprDetailsId).pipe(
      take(1),
      mergeMap((dprDetails) => {
        let dprForecastActivities = cloneDeep(
          dprDetails?.dprForecastActivities.filter((x) => !isNaN(+x.id))
        ) as DprForecastActivity[];

        model.syncDprForecastActivities.addedList.forEach((added) => {
          const newItem: DprForecastActivity = {
            id: added.id,
            dprDetailId: added.dprDetailId,
            forecastCategoryId: added.forecastCategoryId,
            activityGroupId: added.activityGroupId,
            activityId: added.activityId,
            locationId: added.locationId,
            date: added.date,
            start: added.start,
            finish: added.finish,
            comment: added.comment,
            dprForecastSubActivities:
              added.syncDprForecastSubActivities.addedList ?? [],
            dprForecastActivityCustomDataList:
              added.syncDprForecastActivityCustomDataList.addedList ?? [],
          };

          dprForecastActivities.push(newItem);
        });

        model.syncDprForecastActivities.updatedList.forEach((updated) => {
          const dprForecastActivity = dprForecastActivities.find(
            (x: DprForecastActivity) => x.id === updated.id
          );

          if (dprForecastActivity) {
            dprForecastActivity.dprDetailId = updated.dprDetailId;
            dprForecastActivity.forecastCategoryId = updated.forecastCategoryId;
            dprForecastActivity.activityGroupId = updated.activityGroupId;
            dprForecastActivity.activityId = updated.activityId;
            dprForecastActivity.locationId = updated.locationId;
            dprForecastActivity.date = updated.date;
            dprForecastActivity.start = updated.start;
            dprForecastActivity.finish = updated.finish;
            dprForecastActivity.comment = updated.comment;

            dprForecastActivity.dprForecastSubActivities =
              this.mergeService.applyChanges(
                dprForecastActivity.dprForecastSubActivities,
                updated.syncDprForecastSubActivities
              );

            dprForecastActivity.dprForecastActivityCustomDataList =
              this.mergeService.applyChanges(
                dprForecastActivity.dprForecastActivityCustomDataList,
                updated.syncDprForecastActivityCustomDataList
              );
          }
        });

        dprForecastActivities = this.mergeService.applyDeleteChanges(
          dprForecastActivities,
          model.syncDprForecastActivities.deletedIds
        );

        const updatedDprDetails = {
          ...cloneDeep(dprDetails),
          dprForecastActivities,
        } as DprDetails;

        return of({
          dprDetailsId: dprDetails.id,
          dprForecastActivities: updatedDprDetails.dprForecastActivities,
          warningStatusTab: dprDetails.warningStatusTab,
          firstWarning: updatedDprDetails.firstWarning,
        });
      })
    );
  }
  applyDprFreeTextFieldOfflineChanges(
    syncDprFreeTextField: DprFreeTextFieldSync
  ): Observable<DprFreeTextFieldSyncResult> {
    return this.offlineFacade
      .getDprDetails(syncDprFreeTextField.dprFreeTextField.dprDetailId)
      .pipe(
        take(1),
        mergeMap((dprDetails) => {
          const freeTextField: DprFreeTextField = {
            id: syncDprFreeTextField.dprFreeTextField.id,
            dprDetailId: syncDprFreeTextField.dprFreeTextField.dprDetailId,
            freeTextFieldId:
              syncDprFreeTextField.dprFreeTextField.freeTextFieldId,
            dprFreeTextFieldDetails: [],
          };

          const existingDprFreeTextField = dprDetails?.dprFreeTextFields?.find(
            (x) => x.id === syncDprFreeTextField.dprFreeTextField.id
          );

          const dprFreeTextField = cloneDeep(
            existingDprFreeTextField ?? freeTextField
          ) as DprFreeTextField;

          dprFreeTextField.dprFreeTextFieldDetails =
            dprFreeTextField.dprFreeTextFieldDetails?.filter(
              (x) => !isNaN(+x.id)
            );

          dprFreeTextField.dprFreeTextFieldDetails =
            this.mergeService.applyChanges(
              dprFreeTextField.dprFreeTextFieldDetails,
              syncDprFreeTextField.dprFreeTextField.syncDprFreeTextFieldDetails
            );

          const updatedDprDetails = {
            ...cloneDeep(dprDetails),
            dprFreeTextFields: existingDprFreeTextField
              ? dprDetails.dprFreeTextFields?.map((x) =>
                  x.id === dprFreeTextField.id ? dprFreeTextField : x
                )
              : [...dprDetails.dprFreeTextFields, dprFreeTextField],
          } as DprDetails;

          this.dprDetailsWarningService.updateDprWarningStatus(
            updatedDprDetails
          );

          return of({
            ...updatedDprDetails.dprFreeTextFields.find(
              (x) => x.id === dprFreeTextField.id
            ),
            firstWarning: updatedDprDetails.firstWarning,
            warningStatusTap: updatedDprDetails.warningStatusTab,
          });
        })
      );
  }

  applyDprProgressOfflineChanges(
    syncDprProgress: DprProgressGridSync
  ): Observable<DprProgress> {
    return this.offlineFacade
      .getDprDetails(syncDprProgress.dprprogress.dprDetailId)
      .pipe(
        take(1),
        mergeMap((dprDetails) => {
          const dprProgress = cloneDeep(dprDetails?.dprProgress) as DprProgress;
          dprProgress.dprProgressAttachments =
            dprProgress.dprProgressAttachments.filter((x) => !isNaN(+x.id));

          syncDprProgress.dprprogress.syncDprProgressDetails.addedList.forEach(
            (added) => {
              const index = dprProgress.dprProgressDetails.findIndex(
                (x) => x.progressId === added.progressId
              );

              if (index === -1) {
                dprProgress.dprProgressDetails.push(added);
              } else {
                dprProgress.dprProgressDetails[index] = added;
              }
            }
          );

          dprProgress.dprProgressDetails = this.mergeService.applyUpdateChanges(
            dprProgress.dprProgressDetails,
            syncDprProgress.dprprogress.syncDprProgressDetails.updatedList
          );

          dprProgress.dprProgressDetails = this.mergeService.applyDeleteChanges(
            dprProgress.dprProgressDetails,
            syncDprProgress.dprprogress.syncDprProgressDetails.deletedIds
          );

          dprProgress.dprProgressAttachments = this.mergeService.applyChanges(
            dprProgress.dprProgressAttachments,
            syncDprProgress.dprprogress.syncDprProgressAttachments
          );
          dprProgress.comment = syncDprProgress.dprprogress.comment;

          const updatedDprDetails = {
            ...cloneDeep(dprDetails),
            dprProgress,
          } as DprDetails;

          // this is needed for the dpr offline progress service to have the
          // right previous value
          this.offlineFacade.setDprProgress(
            cloneDeep(dprProgress),
            updatedDprDetails.id
          );

          return combineLatest([
            of(updatedDprDetails),
            this.dprProgressOfflineService.updateDependentProgressPrevValues(
              dprDetails.dprDateTime
            ),
          ]);
        }),
        mergeMap(([updatedDprDetails]) => {
          this.dprDetailsWarningService.updateDprWarningStatus(
            updatedDprDetails
          );

          updatedDprDetails.dprProgress.firstWarning =
            updatedDprDetails.firstWarning;
          updatedDprDetails.dprProgress.warningStatusTab =
            updatedDprDetails.warningStatusTab;

          return of(updatedDprDetails.dprProgress);
        })
      );
  }

  applyDprProgessWithoutTargetOfflineChanges(
    syncDprProgressWithoutTarget: DprProgressWithoutTargetGridSync
  ): Observable<DprProgressWithoutTarget> {
    return this.offlineFacade
      .getDprDetails(
        syncDprProgressWithoutTarget.dprProgressWithoutTarget.dprDetailId
      )
      .pipe(
        take(1),
        mergeMap((dprDetails) => {
          const dprProgressWithoutTarget = cloneDeep(
            dprDetails?.dprProgressWithoutTarget
          ) as DprProgressWithoutTarget;
          dprProgressWithoutTarget.dprProgressWithoutTargetAttachments =
            dprProgressWithoutTarget.dprProgressWithoutTargetAttachments.filter(
              (x) => !isNaN(+x.id)
            );

          syncDprProgressWithoutTarget.dprProgressWithoutTarget.syncDprProgressWithoutTargetDetails.addedList.forEach(
            (added) => {
              const index =
                dprProgressWithoutTarget.dprProgressWithoutTargetDetails.findIndex(
                  (x) =>
                    x.progressWithoutTargetId === added.progressWithoutTargetId
                );

              if (index === -1) {
                dprProgressWithoutTarget.dprProgressWithoutTargetDetails.push(
                  added
                );
              } else {
                dprProgressWithoutTarget.dprProgressWithoutTargetDetails[
                  index
                ] = added;
              }
            }
          );

          dprProgressWithoutTarget.dprProgressWithoutTargetDetails =
            this.mergeService.applyUpdateChanges(
              dprProgressWithoutTarget.dprProgressWithoutTargetDetails,
              syncDprProgressWithoutTarget.dprProgressWithoutTarget
                .syncDprProgressWithoutTargetDetails.updatedList
            );

          dprProgressWithoutTarget.dprProgressWithoutTargetDetails =
            this.mergeService.applyDeleteChanges(
              dprProgressWithoutTarget.dprProgressWithoutTargetDetails,
              syncDprProgressWithoutTarget.dprProgressWithoutTarget
                .syncDprProgressWithoutTargetDetails.deletedIds
            );

          dprProgressWithoutTarget.dprProgressWithoutTargetAttachments =
            this.mergeService.applyChanges(
              dprProgressWithoutTarget.dprProgressWithoutTargetAttachments,
              syncDprProgressWithoutTarget.dprProgressWithoutTarget
                .syncDprProgressWithoutTargetAttachments
            );
          dprProgressWithoutTarget.comment =
            syncDprProgressWithoutTarget.dprProgressWithoutTarget.comment;

          const updateDprDetails = {
            ...cloneDeep(dprDetails),
            dprProgressWithoutTarget,
          } as DprDetails;

          this.offlineFacade.setDprProgressWithoutTarget(
            cloneDeep(dprProgressWithoutTarget),
            updateDprDetails.id
          );

          return combineLatest([
            of(updateDprDetails),
            this.dprProgressWithoutTargetService.updateDependentProgressWithoutTargetPrevValues(
              dprDetails.dprDateTime
            ),
          ]);
        }),
        mergeMap(([updateDprDetails]) => {
          this.dprDetailsWarningService.updateDprWarningStatus(
            updateDprDetails
          );

          updateDprDetails.dprProgressWithoutTarget.firstWarning =
            updateDprDetails.firstWarning;
          updateDprDetails.dprProgressWithoutTarget.warningStatusTab =
            updateDprDetails.warningStatusTab;

          return of(updateDprDetails.dprProgressWithoutTarget);
        })
      );
  }

  applyDprAppendixOfflineChanges(
    syncDprAppendix: DprAppendixGridSync
  ): Observable<DprAppendixGridSyncResult> {
    return this.offlineFacade.getDprDetails(syncDprAppendix.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetails) => {
        let dprAppendixAttachments = cloneDeep(
          dprDetails?.dprAppendixAttachments
        );

        dprAppendixAttachments = this.mergeService.applyChanges(
          dprAppendixAttachments,
          syncDprAppendix.syncDprAppendixAttachments
        );

        const updatedDprDetails = {
          ...cloneDeep(dprDetails),
          dprAppendixAttachments,
        } as DprDetails;

        return of({
          dprDetailId: syncDprAppendix.dprDetailId,
          dprAppendixAttachments: updatedDprDetails.dprAppendixAttachments,
          firstWarning: updatedDprDetails.firstWarning,
          warningStatusTab: dprDetails.warningStatusTab,
        });
      })
    );
  }

  applyDprHiddenOfflineChanges(
    model: DprHiddenGridSync
  ): Observable<DprHiddenGridSyncResult> {
    return this.offlineFacade.getDprDetails(model.dprDetailId).pipe(
      take(1),
      mergeMap((dprDetails) => {
        const dprHidden = cloneDeep(dprDetails.dprHidden) as DprHidden[];

        model.syncDprHidden.addedList.forEach((added) => {
          const index = dprHidden.findIndex(
            (x) => x.hiddenSubtabId === added.hiddenSubtabId
          );

          if (index === -1) {
            const newItem = {
              id: added.id,
              dprDetailId: added.dprDetailId,
              comment: added.comment,
              hiddenSubtabId: added.hiddenSubtabId,
              dprHiddenAttachments:
                added.syncDprHiddenAttachments.addedList || [],
            };
            dprHidden.push(newItem);
          } else {
            dprHidden[index] = {
              ...added,
              dprHiddenAttachments:
                added.syncDprHiddenAttachments.addedList || [],
            };
          }
        });

        model.syncDprHidden.updatedList.forEach((updated) => {
          const hidden = dprHidden.find((x) => x.id === updated.id);

          if (hidden) {
            hidden.comment = updated.comment;

            hidden.dprHiddenAttachments = this.mergeService.applyChanges(
              hidden.dprHiddenAttachments,
              updated.syncDprHiddenAttachments
            );
          }
        });

        const updatedDprDetails = {
          ...cloneDeep(dprDetails),
          dprHidden,
        } as DprDetails;

        return of({
          dprDetailId: model.dprDetailId,
          firstWarning: updatedDprDetails.firstWarning,
          warningStatusTab: dprDetails.warningStatusTab,
          dprHidden: updatedDprDetails.dprHidden,
        });
      })
    );
  }

  applyDprTableOfColumnsOfflineChanges(
    syncDprTableOfColumns: DprTableOfColumnGridSync
  ): Observable<DprTableOfColumns> {
    return this.offlineFacade
      .getDprDetails(syncDprTableOfColumns.dprTableOfColumn.dprDetailId)
      .pipe(
        take(1),
        mergeMap((dprDetails) => {
          const emptyDprTableOfColumns = createEmptyDprTableOfColumns(
            syncDprTableOfColumns.dprTableOfColumn.id,
            syncDprTableOfColumns.dprTableOfColumn.dprDetailId,
            syncDprTableOfColumns.dprTableOfColumn.tableOfColumnId
          );

          const existingDprTableOfColumn = dprDetails?.dprTableOfColumns.find(
            (x) =>
              x.tableOfColumnId ===
              syncDprTableOfColumns.dprTableOfColumn.tableOfColumnId
          );

          // create a deep copy to make the object editable
          const dprTableOfColumns = cloneDeep(
            existingDprTableOfColumn ?? emptyDprTableOfColumns
          ) as DprTableOfColumns;

          // A table sent before it had any rows has no id yet; it takes the one the
          // save was made with.
          dprTableOfColumns.id = syncDprTableOfColumns.dprTableOfColumn.id;

          dprTableOfColumns.dprTableOfColumnsDetails =
            dprTableOfColumns.dprTableOfColumnsDetails.filter(
              (x) => !isNaN(+x.id)
            );
          dprTableOfColumns.dprTableOfColumnsAttachment =
            dprTableOfColumns.dprTableOfColumnsAttachment.filter(
              (x) => !isNaN(+x.id)
            );

          dprTableOfColumns.dprTableOfColumnsDetails =
            this.mergeService.applyChanges(
              dprTableOfColumns.dprTableOfColumnsDetails,
              syncDprTableOfColumns.dprTableOfColumn.syncDprTableOfColumnDetails
            );
          dprTableOfColumns.dprTableOfColumnsAttachment =
            this.mergeService.applyChanges(
              dprTableOfColumns.dprTableOfColumnsAttachment,
              syncDprTableOfColumns.dprTableOfColumn
                .syncDprTableOfColumnAttachments
            );
          dprTableOfColumns.comment =
            syncDprTableOfColumns.dprTableOfColumn.comment;

          const updatedDprDetails = {
            ...cloneDeep(dprDetails),
            dprTableOfColumns: existingDprTableOfColumn
              ? dprDetails.dprTableOfColumns.map((x) =>
                  x.tableOfColumnId === dprTableOfColumns.tableOfColumnId
                    ? dprTableOfColumns
                    : x
                )
              : [...dprDetails.dprTableOfColumns, dprTableOfColumns],
          } as DprDetails;

          this.dprDetailsWarningService.updateDprWarningStatus(
            updatedDprDetails
          );

          updatedDprDetails.dprTableOfColumns.find(
            (x) => x.id === dprTableOfColumns.id
          ).firstWarning = updatedDprDetails.firstWarning;
          updatedDprDetails.dprTableOfColumns.find(
            (x) => x.id === dprTableOfColumns.id
          ).warningStatusTab = updatedDprDetails.warningStatusTab;

          return this.dprAccumulationOfflineService
            .updateDependentPreviousValues(updatedDprDetails, {
              kind: 'tableOfColumns',
              tableOfColumnId: dprTableOfColumns.tableOfColumnId,
            })
            .pipe(
              map(() =>
                updatedDprDetails.dprTableOfColumns.find(
                  (x) => x.id === dprTableOfColumns.id
                )
              )
            );
        })
      );
  }

  //#region Private methods

  /**
   * The grid never sends derived cells, so a stored carry-over or formula value
   * would outlive an edit to the inputs it was worked out from. The derived cells
   * of every status tracker the save touched are dropped, as an edited Table of
   * Columns row loses its own.
   */
  private withoutEditedDerivedCells(
    details: DprStatusTrackerDetails[],
    storedDetails: DprStatusTrackerDetails[],
    syncDetails: Sync<DprStatusTrackerDetails>,
    statusTrackers: StatusTrackerWithData[]
  ): DprStatusTrackerDetails[] {
    const deletedIds = new Set(syncDetails.deletedIds.map(String));
    const editedStatusTrackerIds = new Set(
      [
        ...syncDetails.addedList,
        ...syncDetails.updatedList,
        ...storedDetails.filter((detail) => deletedIds.has(String(detail.id))),
      ].map((detail) => +detail.statusTrackerId)
    );

    return withoutDerivedStatusTrackerCells(
      details,
      statusTrackers?.filter((statusTracker) =>
        editedStatusTrackerIds.has(+statusTracker.id)
      )
    );
  }

  //#endregion
}
